import {inject, Injectable} from '@angular/core';
import {DynamoHttpService} from '@skillgmbh/dynamo';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, map, shareReplay, take} from 'rxjs/operators';
import {Product} from '../../lib/gastroflow/interfaces/Product';

export type Weekday = 'Montag' | 'Dienstag' | 'Mittwoch' | 'Donnerstag' | 'Freitag';

export interface Menu {
    vorspeise?: string;
    hauptspeise?: string;
    preis?: string;
}

export interface WeekdayMenu {
    day: Weekday;
    menu1?: Menu;
    menu2?: Menu;
}

export interface WeeklyMenu {
    menu: Menu;
}

export const WEEKDAYS: Weekday[] = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag'];

/**
 * Single source of truth for the Gastroflow menu data.
 * Results are cached so the start page teaser and the Speiseplan page
 * share one request per category.
 */
@Injectable({providedIn: 'root'})
export class MenuService {
    private readonly httpService: DynamoHttpService = inject(DynamoHttpService);

    private weekdayMenus$?: Observable<WeekdayMenu[]>;
    private weeklyMenus$?: Observable<WeeklyMenu[]>;

    getWeekdayMenus(): Observable<WeekdayMenu[]> {
        if (!this.weekdayMenus$) {
            this.weekdayMenus$ = forkJoin({
                menu1: this.fetchCategory('Menü 1'),
                menu2: this.fetchCategory('Menü 2')
            }).pipe(
                map(({menu1, menu2}) => WEEKDAYS.map((day: Weekday): WeekdayMenu => ({
                    day: day,
                    menu1: this.toMenu(this.findProductForDay(menu1, day)),
                    menu2: this.toMenu(this.findProductForDay(menu2, day))
                }))),
                shareReplay({bufferSize: 1, refCount: false})
            );
        }

        return this.weekdayMenus$;
    }

    getWeeklyMenus(): Observable<WeeklyMenu[]> {
        if (!this.weeklyMenus$) {
            this.weeklyMenus$ = this.fetchCategory('Wochenkarte').pipe(
                map((products: Product[]): WeeklyMenu[] => products.map((product: Product): WeeklyMenu => ({
                    menu: {
                        hauptspeise: `${product.name} ${product.description}`.trim(),
                        preis: this.formatPrice(product.price)
                    }
                }))),
                shareReplay({bufferSize: 1, refCount: false})
            );
        }

        return this.weeklyMenus$;
    }

    /** The current weekday, or undefined on weekends. */
    getCurrentWeekday(): Weekday | undefined {
        const dayIndex: number = new Date().getDay();

        return WEEKDAYS[dayIndex - 1];
    }

    private fetchCategory(category: string): Observable<Product[]> {
        return this.httpService.get('gastroflow-proxy', 'rettensteiner', category)
            .pipe(
                take(1),
                map((products: Product[]): Product[] => products || []),
                catchError((): Observable<Product[]> => of([]))
            );
    }

    private findProductForDay(products: Product[], weekday: Weekday): Product | undefined {
        const shortDay: string = weekday.slice(0, 2).toUpperCase();

        return products.find((product: Product) => product.weekdays?.some(productWeekday => productWeekday.day === shortDay));
    }

    private toMenu(product: Product | undefined): Menu {
        if (!product) {
            return {hauptspeise: '', preis: ''};
        }

        const starters: string[] = this.getAvailableStarters(product);

        return {
            vorspeise: starters.length ? starters.join(' oder ') : undefined,
            hauptspeise: `${product.name} ${product.description}`.trim(),
            preis: this.formatPrice(product.price)
        };
    }

    private getAvailableStarters(product: Product): string[] {
        const options = product.defaultOptionGroup?.options || [];

        return options
            .filter(option => option.name.toLowerCase() !== 'keine vorspeise')
            .sort((a, b) => {
                if (a.name.toLowerCase().includes('salad') || a.name.toLowerCase().includes('salat')) return -1;
                if (b.name.toLowerCase().includes('salad') || b.name.toLowerCase().includes('salat')) return 1;
                return 0;
            })
            .map(option => option.name);
    }

    private formatPrice(price: number | string): string {
        const parsed: number = parseFloat(`${price}`);

        if (isNaN(parsed)) {
            return '';
        }

        return parsed.toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }
}
